import { Injectable } from '@angular/core';
import { Auth } from 'aws-amplify';
import { BehaviorSubject, Observable, from, tap, map, catchError, of } from 'rxjs';
import { DialogService } from './dialog.service';
import { ErrorService } from './error.service';
import { NavigationService } from './navigation.service';
import { CognitoUser } from 'amazon-cognito-identity-js';
import { AuthForgotPssConfirmDialogComponent } from '../auth-forgot-pss/auth-forgot-pss-confirm-dialog/auth-forgot-pss-confirm-dialog.component';
import { AuthForgotPssDialogComponent } from '../auth-forgot-pss/auth-forgot-pss-dialog/auth-forgot-pss-dialog.component';
import { AUTH_PSS_RESET_SUCCESS_SUBTITLE, AUTH_PSS_RESET_SUCCESS_TITLE } from '../shared/constants/translations';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  loggedIn: BehaviorSubject<boolean>;
  pending_cognito_user?: CognitoUser;

  constructor(
    private navigation_service: NavigationService,
    private dialog_service: DialogService,
    private error_service: ErrorService,
  ) {
    this.loggedIn = new BehaviorSubject<boolean>(false);
  }

  buildCognitoCustomer(username: string, password: string, invitation_id?: string): any {
    if (invitation_id) {
      return {
        username,
        password,
        attributes: {
          'custom:invitation_id': invitation_id,
        },
      };
    }
    return {
      username,
      password,
    };
  }

  signup(cognito_customer: any): Observable<any> {
    return from(Auth.signUp(cognito_customer));
  }

  signin(username: string, password: string): Observable<any> {
    return from(Auth.signIn(username, password)).pipe(
      tap(() => {
        this.loggedIn.next(true);
      }),
    );
  }

  confirmSignin(cognito_customer: any, otp: string): Observable<any> {
    return from(Auth.confirmSignIn(cognito_customer, otp));
  }

  getAuthenticatedUser(): Observable<any> {
    return from(Auth.currentAuthenticatedUser());
  }

  isAuthenticated(): Observable<any> {
    return from(Auth.currentAuthenticatedUser()).pipe(
      map(result => {
        this.loggedIn.next(true);
        return true;
      }),
      catchError(error => {
        this.loggedIn.next(false);
        return of(false);
      }),
    );
  }

  signout(): Observable<any> {
    return from(Auth.signOut()).pipe(
      tap(() => {
        this.loggedIn.next(false);
        this.navigation_service.toAuthSignin();
      }),
    );
  }

  confirmOtp(username: string, code: string): Observable<any> {
    return from(Auth.confirmSignUp(username, code));
  }

  resendOtp(username: string): Observable<any> {
    return from(Auth.resendSignUp(username));
  }

  completeNewPassword(user: CognitoUser, new_password: string): Observable<any> {
    return from(Auth.completeNewPassword(user, new_password));
  }

  forgotPassword(username: string): Observable<any> {
    return from(Auth.forgotPassword(username));
  }

  forgotPasswordSubmit(username: string, code: string, newPassword: string): Observable<any> {
    return from(Auth.forgotPasswordSubmit(username, code, newPassword));
  }

  changePassword(user: CognitoUser, old_password: string, new_password: string): Observable<any> {
    return from(Auth.changePassword(user, old_password, new_password));
  }

  onForgotPssClick(): void {
    this.dialog_service
      .openDialog(AuthForgotPssDialogComponent)
      ?.afterClosed()
      .subscribe({
        next: dialog_result => {
          if (dialog_result && dialog_result.success) {
            this.onForgotPssConfirmClick(dialog_result.username);
          } else if (dialog_result && dialog_result.error) {
            this.error_service.processApiError(dialog_result.error.code, dialog_result.error.message);
          }
        },
      });
  }

  onForgotPssConfirmClick(username: string): void {
    this.dialog_service
      .openDialog(AuthForgotPssConfirmDialogComponent, {
        username: username,
      })
      ?.afterClosed()
      .subscribe({
        next: dialog_result => {
          if (dialog_result && dialog_result.success) {
            this.dialog_service.openInfoDialog(AUTH_PSS_RESET_SUCCESS_SUBTITLE, AUTH_PSS_RESET_SUCCESS_TITLE);
          } else if (dialog_result && dialog_result.error) {
            this.error_service.processApiError(dialog_result.error.code, dialog_result.error.message);
          }
        },
      });
  }
}
